import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useThemeMode } from '../../hooks/useThemeMode';
import { OrganizationSwitcher } from '../layout/OrganizationSwitcher';
import taskService from '../../services/task.service';
import { NAV_ITEMS } from './nav';
import { cn } from '../../lib/cn';

function openPalette() {
  document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', ctrlKey: true, bubbles: true }));
}

export const Sidebar: React.FC = () => {
  const { user, logout } = useAuth();
  const { mode, setMode } = useThemeMode();
  const [overdue, setOverdue] = useState(0);

  useEffect(() => {
    let live = true;
    taskService.getTaskStats()
      .then((s) => { if (live) setOverdue(s?.overdueTasks ?? 0); })
      .catch(() => { if (live) setOverdue(0); });
    return () => { live = false; };
  }, []);

  const initials = `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}` || 'U';
  const nextMode = mode === 'light' ? 'dark' : mode === 'dark' ? 'system' : 'light';

  return (
    <aside className="hidden h-screen w-52 shrink-0 flex-col border-r border-rule-faint bg-paper md:flex sticky top-0"
      data-testid="sidebar">
      <div className="px-3 pt-4">
        <div className="px-2 font-serif text-scale-3 font-semibold text-ink">RiskMatrix</div>
        <div className="mt-3"><OrganizationSwitcher /></div>
      </div>

      <nav className="mt-4 flex flex-col gap-0.5 px-3">
        {NAV_ITEMS.map((n) => (
          <NavLink key={n.path} to={n.path} data-testid={n.testId}
            className={({ isActive }) =>
              cn(
                'flex items-center justify-between border-l-2 border-transparent px-2 py-1.5 font-sans text-scale-1 text-ink-muted hover:text-ink',
                isActive && 'border-accent bg-accent-soft font-semibold text-accent'
              )
            }>
            {n.label}
            {n.label === 'Tasks' && overdue > 0 && (
              <span className="bg-accent px-1.5 font-mono text-scale-0 text-paper">{overdue}</span>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="mt-auto flex flex-col gap-3 border-t border-rule-faint px-3 py-3">
        <button type="button" onClick={openPalette}
          className="flex w-full items-center justify-between border border-rule-faint px-2 py-1.5 text-scale-1 text-ink-muted hover:text-ink">
          Search…
          <kbd className="border border-rule-faint px-1 font-mono text-scale-0">⌘K</kbd>
        </button>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-accent font-sans text-scale-0 text-paper">
              {initials}
            </span>
            <span className="font-sans text-scale-1 text-ink">
              {user?.firstName} {user?.lastName}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button type="button" title={`Theme: ${mode} (click for ${nextMode})`}
              onClick={() => setMode(nextMode)}
              className="px-1 text-scale-2 text-ink-muted hover:text-ink" data-testid="theme-toggle">
              ◐
            </button>
            <button type="button" title="Sign out" onClick={() => logout()}
              className="px-1 text-scale-2 text-ink-muted hover:text-accent" data-testid="logout">
              ⎋
            </button>
          </div>
        </div>
      </div>
    </aside>
  );
};
